import { useLanguage } from '../context/LanguageContext';
import { Instagram, Mail, Heart, MessageCircle, Facebook, Accessibility, FileText, Shield } from 'lucide-react';

// Logo image URL
const LOGO_URL = "/logo-transparent.png";

const Footer = ({ onAccessibilityClick, onTermsClick, onPrivacyClick }) => {
  const { t, language } = useLanguage();

  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="py-12 md:py-16"
      style={{ backgroundColor: 'var(--charcoal)' }}
      data-testid="footer"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-3 gap-10 md:gap-8 text-center md:text-start">
          {/* Brand with Logo */}
          <div className={language === 'he' ? 'md:text-right flex flex-col items-end' : 'flex flex-col items-start'}>
            <img 
              src={LOGO_URL}
              alt="Eran MH Coaching"
              className="h-44 md:h-48 w-auto mb-4"
              style={{ filter: 'invert(1) brightness(2)' }}
              data-testid="footer-logo"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              {language === 'he' 
                ? 'אימון בשיטת סאטיה - מסע של חופש פנימי והתעוררות'
                : 'Satya Method Coaching - A journey of inner freedom and awakening'
              }
            </p>
          </div>

          {/* Follow */}
          <div className={`${language === 'he' ? 'md:text-right' : ''}`}>
            <h4 className="text-white font-medium mb-4">{t('footer_follow')}</h4>
            <a
              href="https://instagram.com/eranmh_coach"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={20} />
              <span>@eranmh_coach</span>
            </a>
            <a
              href="https://www.facebook.com/share/14Y2Cc29dmh/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook size={20} />
              <span>EranMH Coach</span>
            </a>
          </div>

          {/* Contact */}
          <div className={language === 'he' ? 'md:text-right' : ''}>
            <h4 className="text-white font-medium mb-4">{t('footer_contact')}</h4>
            <div className="space-y-3">
              <a
                href="https://wa.me/34671295437"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors justify-center md:justify-end"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={20} />
                <span dir="ltr">+34 671 295 437</span>
              </a>
              <a
                href="mailto:eran.mazor86@gmail.com"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors justify-center md:justify-end"
                data-testid="footer-email"
              >
                <Mail size={20} />
                <span dir="ltr">eran.mazor86@gmail.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div 
          className="border-t my-8 md:my-10"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        />

        {/* Copyright & Legal */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center">
          <p className="text-gray-500 text-sm">
            © {currentYear} Eran MH Coaching. {t('footer_rights')}.
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={onAccessibilityClick}
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors flex items-center gap-1"
              data-testid="footer-accessibility"
            >
              <Accessibility size={14} />
              {language === 'he' ? 'הצהרת נגישות' : 'Accessibility'}
            </button>
            <span className="text-gray-600">|</span>
            <button
              onClick={onTermsClick}
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors flex items-center gap-1"
              data-testid="footer-terms"
            >
              <FileText size={14} />
              {language === 'he' ? 'תנאי שימוש' : 'Terms of Use'}
            </button>
            <span className="text-gray-600">|</span>
            <button
              onClick={onPrivacyClick}
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors flex items-center gap-1"
              data-testid="footer-privacy"
            >
              <Shield size={14} />
              {language === 'he' ? 'מדיניות פרטיות' : 'Privacy Policy'}
            </button>
          </div>
          <p className="text-gray-500 text-sm flex items-center gap-1">
            {language === 'he' ? 'נבנה עם' : 'Built with'}
            <Heart size={14} className="text-red-400" />
            {language === 'he' ? 'בשיטת סאטיה' : 'in Satya'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
